<script lang="ts">
  import { listClinicalGroups } from '$lib/api/classes';
  import type { ClinicalGroupSummary } from '$lib/api/types';
  import { untrack } from 'svelte';
  import QuickSearch from './QuickSearch.svelte';

  interface Props {
    id: string;
    classId: number | null;
    value: string | null;
    valueText?: string | null;
    disabled?: boolean;
    filterClinicalGroups?: boolean; // if true, only clinical groups with no instructor set will be listed. used when
    // adding an instructor to a class.
    required?: boolean;
    touched?: boolean;
  }
  let {
    id,
    classId,
    value = $bindable(null),
    valueText = null,
    disabled = false,
    filterClinicalGroups = false,
    required,
    touched = $bindable(false),
  }: Props = $props();

  // all clinical groups of class are loaded when the selected class changes, without pagination in the load function
  let classClinicalGroupsPromise: Promise<ClinicalGroupSummary[]> | null =
    classId === null ? null : listClinicalGroups(classId);
  let quicksearch: QuickSearch;

  let initialEffectCall = $state(true);

  $effect(() => {
    // we need this effect to only run when classId changes but not the first time this effect is mounted
    classId;

    if (initialEffectCall) return () => (initialEffectCall = false);

    quicksearch.clearSelection();
    if (classId === null) {
      classClinicalGroupsPromise = null;
      untrack(() => {
        quicksearch.forceRefresh();
      });
      return;
    }
    classClinicalGroupsPromise = listClinicalGroups(classId);
    classClinicalGroupsPromise.then(() => quicksearch.forceRefresh());
  });
</script>

<QuickSearch
  bind:this={quicksearch}
  bind:value
  {valueText}
  {disabled}
  {id}
  bind:touched={touched}
  invalid={required && touched && value === null}
  feedback="Please choose a clinical group"
  placeholder="Choose a clinical group"
  minimumQueryLength={0}
  loadData={async (query, page, pageSize, signal) => {
    if (classClinicalGroupsPromise === null) {
      return [];
    }

    if (page > 1) return []; //disables pagination

    let clinicalGroups = await classClinicalGroupsPromise;

    if (filterClinicalGroups) {
      clinicalGroups = clinicalGroups.filter(x => x.instructorId === undefined);
    }

    if (value === null && query && query.length > 0) {
      const queryLc = query.trim().toLowerCase();
      clinicalGroups = clinicalGroups.filter(x => x.name.toLowerCase().includes(queryLc));
    }

    return clinicalGroups.map(x => {
      return {
        value: x.id.toString(),
        text: x.name,
        title: x.name,
        subtitle:
          x.instructorId !== undefined
            ? `${x.studentCount} student(s), instructed by ${x.instructorFirstName} ${x.instructorLastName}`
            : `${x.studentCount} student(s)`,
      };
    });
  }}
  emptySearchResultsLabel="No clinical groups found!"
  beginSearchingLabel="Enter a clinical group name or code to begin searching"
/>
